/* eslint-disable no-console */
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { FormType, FormErrors, getLocalesFromIsoLocale, IsoLocale } from "@simplyk/common";
import { v4 } from "uuid";

import { MinimalLayout } from "../../../common/layouts/MinimalLayout";
import { ServerSideContext } from "../../../common/types/app";

import { getSSRDonationForm, getTranslation, provideOrganization } from "@/common/helpers/getStaticProps";
import { useTheme } from "@/common/hooks/useTheme";
import { DonationFormFrontendPageOutput } from "@/common/types/trpc";
import { AppFormProvider } from "@/components/AppProvider/AppFormProvider/AppFormProvider";
import { ZeffyHead } from "@/components/Head/ZeffyHead";
import NotFoundWithProviders from "@/components/NotFound/NotFoundWithProviders";
import { Thermometer } from "@/components/Thermometer/Thermometer";

const EMBED_THERMOMETER_REFRESH_INTERVAL_MS = 60_000;

type EmbedThermometerPageProps =
  | {
      donationForm: DonationFormFrontendPageOutput;
      isDeleted?: false;
    }
  | {
      donationForm?: null;
      isDeleted: true;
    };

const EmbedThermometerPage = ({ donationForm, isDeleted }: EmbedThermometerPageProps) => {
  const theme = useTheme({ color: donationForm?.color, transparent: true });

  if (isDeleted) {
    return (
      <>
        <ZeffyHead />
        <NotFoundWithProviders notFoundEntity="form" />
      </>
    );
  }

  return (
    <AppFormProvider
      theme={theme}
      isEmbed={true}
      organization={donationForm.organization}
      formType={FormType.DonationForm}
      formData={donationForm}
      themeColor={donationForm.color}
      fields={[]}
      isDisplayOnly
    >
      <MinimalLayout hideBackground>
        <Container>
          <Grid container>
            <Thermometer
              max={donationForm.target}
              volume={Number(donationForm.formVolume) || 0}
              refreshIntervalMs={EMBED_THERMOMETER_REFRESH_INTERVAL_MS}
              form={{ id: donationForm.id, formType: FormType.DonationForm }}
              gutterBottom
            />
          </Grid>
        </Container>
      </MinimalLayout>
    </AppFormProvider>
  );
};

export const getServerSideProps = async (context: ServerSideContext<{ donationFormId: string }>) => {
  const requestId = (context.req?.headers?.["x-request-id"] as string) || v4();
  const { donationFormId } = context.params;
  const locale = getLocalesFromIsoLocale(context.locale as IsoLocale);

  try {
    const [donationFormResponse, translations] = await Promise.all([
      getSSRDonationForm({ donationFormIdOrUrlPath: donationFormId, requestId }),
      getTranslation(context.locale, ["common", "donationForm", "ticketing"]),
    ]);

    if (donationFormResponse.error) {
      const code = donationFormResponse.error.code;

      if (code === FormErrors.FormDeleted) {
        if (context.res) {
          context.res.statusCode = 410;
        }

        console.log(`Form deleted - redirecting to gone donationFormId=${donationFormId} ssrId=${requestId}`);
        return {
          props: {
            ...translations,
            isDeleted: true,
          },
        };
      }

      console.log(
        `NEXT_ERROR: form not found - redirecting to notFound donationFormId=${donationFormId} ssrId=${requestId}`
      );
      return { props: {}, notFound: true };
    }

    return {
      props: {
        ...translations,
        donationForm: donationFormResponse.data.organization
          ? donationFormResponse.data
          : provideOrganization(donationFormResponse.data, locale),
      },
    };
  } catch (error) {
    console.log(`NEXT_ERROR: request failed - redirecting to notFound ssrId=${requestId}`, error);
    return { props: {}, notFound: true };
  }
};

export default EmbedThermometerPage;
